import { Column, Entity, Index, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { DashboardFilterEntity } from './dashboard-filter.entity';
import type { DashboardPortalSiteEntity } from './dashboard-portal-site.entity';
import type { DashboardRoleEntity } from './dashboard-role.entity';
import type { DashboardUserEntity } from './dashboard-user.entity';
import { DashboardWidgetEntity } from './dashboard-widget.entity';
import { UserDashboardPreferencesEntity } from './user-dashboard-preferences.entity';

export type DashboardVisibility = 'private' | 'shared' | 'organization';

/**
 * `CRM` is the historical default — a dashboard rendered inside the
 * CRM app. With `_owner_userId` set it's a personal rep dashboard;
 * with `_owner_userId` null it's the tenant-default CRM dashboard.
 *
 * `STOREFRONT` is a design rendered on a portal-site homepage. With
 * one or more `dashboard_portal_site` rows pointing at it, it's the
 * design for those specific portals; with zero rows it's the
 * tenant-default storefront design used for any portal that doesn't
 * have a more specific assignment. A storefront design has no
 * meaningful `_owner_userId` (admins author it on behalf of the
 * tenant, not their personal account).
 */
export type DashboardScope = 'CRM' | 'STOREFRONT';

@Index('idx_owner_default', ['ownerUserId', 'isDefault'], {})
@Index('idx_dashboard_visibility', ['visibility'])
@Index('idx_sort', ['sortOrder'], {})
@Index('idx_dashboard_scope', ['scope'])
@Entity('dashboard')
export class DashboardEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('json', { name: 'layoutConfig', nullable: true })
	public layoutConfig!: object | null;

	/**
	 * Where this dashboard is rendered. Defaults to `CRM` so existing
	 * rows behave as before without migration. Storefront designer
	 * UI creates new rows with `STOREFRONT`. Filtering on this column
	 * is what keeps CRM and storefront widget palettes / selectors
	 * separate (per the design: storefront ≠ CRM, ever).
	 */
	@Column('varchar', { name: 'scope', length: 16, default: () => "'CRM'" })
	public scope!: DashboardScope;

	@Column('enum', {
		name: 'visibility',
		enum: ['private', 'shared', 'organization'],
		default: 'private',
	})
	public visibility!: DashboardVisibility;

	@Column('tinyint', {
		name: 'allowEdit',
		width: 1,
		default: () => "'0'",
	})
	public allowEdit!: boolean;

	@Column('tinyint', {
		name: 'isDefault',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public isDefault!: boolean | null;

	@Column('int', {
		name: 'sortOrder',
		nullable: true,
		unsigned: true,
		default: () => "'0'",
	})
	public sortOrder!: number | null;

	@OneToMany(
		() => DashboardFilterEntity,
		(dashboardFilter) => dashboardFilter.fkDashboard,
		{},
	)
	public dashboardFilters!: Array<DashboardFilterEntity>;

	@OneToMany(
		() => DashboardWidgetEntity,
		(dashboardWidget) => dashboardWidget.fkDashboard,
		{},
	)
	public dashboardWidgets!: Array<DashboardWidgetEntity>;

	@OneToMany(
		() => UserDashboardPreferencesEntity,
		(userDashboardPreferences) => userDashboardPreferences.fkDashboard,
		{},
	)
	public userDashboardPreferences!: Array<UserDashboardPreferencesEntity>;

	@OneToMany(
		'DashboardRoleEntity',
		(role: DashboardRoleEntity) => role.dashboard,
	)
	public sharedRoles!: Array<DashboardRoleEntity>;

	@OneToMany(
		'DashboardUserEntity',
		(user: DashboardUserEntity) => user.dashboard,
	)
	public sharedUsers!: Array<DashboardUserEntity>;

	/**
	 * Portal sites this dashboard serves (only meaningful when
	 * `scope = 'STOREFRONT'`). Empty for CRM dashboards. Empty for a
	 * storefront design means it's the tenant-default fallback (used
	 * when a portal has no specific design assigned). Each portal can
	 * appear in at most one design's set (enforced by UNIQUE on the
	 * join table).
	 */
	@OneToMany(
		'DashboardPortalSiteEntity',
		(link: DashboardPortalSiteEntity) => link.dashboard,
	)
	public portalSiteLinks!: Array<DashboardPortalSiteEntity>;
}
